import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { PlanItem, Customer } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { ColumnDef } from '@tanstack/react-table';
import { DollarSign, Receipt, Info, Loader2, CheckCircle2 } from 'lucide-react';

// The subscriptions view, honest. There is no GET /admin/subscriptions endpoint,
// so the list is derived from the real customer records (each carries its plan +
// billing status). The one real action is recording an invoice payment, which
// the backend turns into an active/renewed subscription + a manual payment — the
// paper-invoice flow MWM actually runs on.

export const SubscriptionsPage: React.FC = () => {
  const { can } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Record-payment form
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [planSlug, setPlanSlug] = useState('');
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [amount, setAmount] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cData, pData] = await Promise.all([api.getAllCustomers(), api.getPlans()]);
      setCustomers(cData);
      setPlans(pData.filter((p) => p.isActive));
    } catch (err) {
      console.error('Failed to load subscriptions', err);
    } finally {
      setLoading(false);
    }
  };

  // Backend search finds any customer (email/name/phone), not just loaded ones.
  // Empty term restores the full list. The pills/plan filter then narrow the set.
  const runSearch = async (term: string) => {
    try {
      setCustomers(term.trim() ? await api.searchCustomers(term) : await api.getAllCustomers());
    } catch (err) {
      console.error('Subscription search failed', err);
    }
  };

  // Keep amount in step with the selected plan + cycle (an operator can override).
  const syncAmount = (slug: string, c: 'monthly' | 'yearly') => {
    const p = plans.find((x) => x.slug === slug);
    if (p) setAmount(c === 'yearly' ? p.priceYearlyUsd : p.priceMonthlyUsd);
  };

  const openForm = (preselectUserId?: string) => {
    const firstPlan = plans[0]?.slug ?? '';
    setUserId(preselectUserId ?? customers[0]?.id ?? '');
    setPlanSlug(firstPlan);
    setCycle('monthly');
    setNotes('');
    setFormError(null);
    syncAmount(firstPlan, 'monthly');
    setOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (amount <= 0) { setFormError('Amount must be greater than 0.'); return; }
    setSaving(true);
    setFormError(null);
    try {
      await api.recordManualPayment({ userId, planSlug, billingCycle: cycle, amount, notes });
      setOpen(false);
      const c = customers.find((x) => x.id === userId);
      const p = plans.find((x) => x.slug === planSlug);
      setFeedback(`Recorded $${amount.toFixed(2)} for ${c?.name ?? 'customer'} — ${p?.name ?? planSlug} (${cycle}) is now active.`);
      loadData();
    } catch (err: any) {
      setFormError(err?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const [filter, setFilter] = usePersistentState<string>('subscriptions.filter', 'all');
  const [planFilter, setPlanFilter] = useState<string>('all');

  const hasPlan = (c: Customer) => !!c.activePlan && c.activePlan !== '—';
  const onPlan = useMemo(() => customers.filter(hasPlan).length, [customers]);
  // Days until the paid period ends (null when no renewal date).
  const daysToRenew = (c: Customer): number | null => {
    if (!c.renewalDate) return null;
    const t = new Date(c.renewalDate).getTime();
    if (isNaN(t)) return null;
    return Math.ceil((t - Date.now()) / 86400000);
  };
  // "Expiring soon" = active plan whose period ends within 7 days: the operator's
  // window to collect + record the next invoice before it lapses to past_due.
  const isExpiring = (c: Customer) => {
    if (c.billingStatus !== 'active') return false;
    const d = daysToRenew(c);
    return d !== null && d <= 7;
  };
  const expiringCount = useMemo(() => customers.filter(isExpiring).length, [customers]);
  const pastDueCount = useMemo(() => customers.filter((c) => c.billingStatus === 'past_due').length, [customers]);
  // Cloud paused = a plan that lapsed (canceled) but the account still has
  // cameras — cloud recording is now withheld for them (see the ingest gate).
  // These are the accounts to chase for a renewal.
  const cloudPaused = (c: Customer) => c.billingStatus === 'canceled' && c.cameraCount > 0;
  const cloudPausedCount = useMemo(() => customers.filter(cloudPaused).length, [customers]);
  // Distinct plan names actually present on customers, for the plan-type filter.
  const planNames = useMemo(
    () => Array.from(new Set(customers.filter(hasPlan).map((c) => c.activePlan))).sort(),
    [customers],
  );

  const filtered = useMemo(() => {
    let list = customers;
    switch (filter) {
      case 'on_plan': list = list.filter(hasPlan); break;
      case 'no_plan': list = list.filter((c) => !hasPlan(c)); break;
      case 'expiring': list = list.filter(isExpiring); break;
      case 'cloud_paused': list = list.filter(cloudPaused); break;
      case 'active': list = list.filter((c) => c.billingStatus === 'active'); break;
      case 'past_due': list = list.filter((c) => c.billingStatus === 'past_due'); break;
      case 'canceled': list = list.filter((c) => c.billingStatus === 'canceled'); break;
    }
    if (planFilter !== 'all') list = list.filter((c) => c.activePlan === planFilter);
    return list;
  }, [customers, filter, planFilter]);

  const columns = useMemo<ColumnDef<Customer>[]>(() => [
    {
      id: 'customer',
      // Include email + phone in the searchable value so a search by email
      // ("primeexcellence…") matches, not just the display name.
      accessorFn: (c) => `${c.name} ${c.email} ${c.phone ?? ''}`,
      header: 'Customer',
      cell: (info) => {
        const c = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{c.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{c.email}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'activePlan',
      header: 'Plan',
      cell: (info) => {
        const v = info.getValue() as string;
        return v && v !== '—' ? <span style={{ fontWeight: 500 }}>{v}</span> : <span style={{ color: 'var(--text-dim)' }}>No plan</span>;
      },
    },
    { accessorKey: 'billingStatus', header: 'Status', cell: (info) => <StatusBadge status={(info.getValue() as string) || 'none'} /> },
    { accessorKey: 'cameraCount', header: 'Cameras', cell: (info) => <span style={{ color: 'var(--primary-light)', fontWeight: 600 }}>{info.getValue() as number}</span> },
    {
      accessorKey: 'renewalDate',
      header: 'Period ends',
      cell: (info) => {
        const c = info.row.original;
        const v = c.renewalDate;
        if (!v) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
        const d = daysToRenew(c);
        const overdue = d !== null && d < 0;
        const soon = c.billingStatus === 'active' && d !== null && d >= 0 && d <= 7;
        const color = overdue ? 'var(--danger-text)' : soon ? 'var(--warning-text)' : 'var(--text-muted)';
        const note = d === null ? '' : overdue ? ` (${-d}d overdue)` : d === 0 ? ' (today)' : ` (${d}d)`;
        return <span style={{ fontSize: 12, color }}>{new Date(v).toLocaleDateString()}{note}</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => (
        <button className="btn btn-secondary btn-sm" onClick={() => openForm(info.row.original.id)} disabled={!can('billing:comp')}>
          <Receipt size={13} /> <span>Record payment</span>
        </button>
      ),
    },
  ], [can, plans, customers]);

  return (
    <div>
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-main)' }}>Subscriptions & invoice payments</h2>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Recording an invoice payment activates or renews the customer's plan and writes a manual payment to the ledger.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => openForm()} disabled={!can('billing:comp') || plans.length === 0}>
          <DollarSign size={14} /> <span>Record invoice payment</span>
        </button>
      </div>

      {feedback && (
        <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success-text)', padding: '10px 16px', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={15} /> <span>{feedback}</span>
        </div>
      )}

      <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', color: 'var(--info-text)', padding: '9px 14px', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Info size={14} style={{ marginTop: 1, flexShrink: 0 }} />
        <span>{onPlan} of {customers.length} customers are on a plan (most have never subscribed). Derived from customer records — there's no subscription-list endpoint yet. Record a payment for anyone to activate/renew their plan.</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { v: 'all', label: `All (${customers.length})` },
          { v: 'on_plan', label: `On a plan (${onPlan})` },
          { v: 'no_plan', label: `No plan (${customers.length - onPlan})` },
          { v: 'expiring', label: `Expiring ≤7d (${expiringCount})` },
          { v: 'past_due', label: `Past due (${pastDueCount})` },
          { v: 'cloud_paused', label: `Cloud paused (${cloudPausedCount})` },
          { v: 'active', label: 'Active' },
          { v: 'canceled', label: 'Canceled' },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`btn btn-sm ${filter === f.v ? 'btn-primary' : 'btn-secondary'}`}
          >
            {f.label}
          </button>
        ))}
        <select
          className="select"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          style={{ marginLeft: 6 }}
          title="Filter by plan"
        >
          <option value="all">All plans</option>
          {planNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={22} className="spin" /></div>
      ) : (
        <DataTable storageKey="subscriptions" data={filtered} columns={columns} searchPlaceholder="Search customer, plan..." exportFilename="mwm_subscriptions.csv" onSearch={runSearch} />
      )}

      {/* Record invoice payment → activate/renew plan */}
      <Modal isOpen={open} onClose={() => setOpen(false)} title="Record invoice payment" subtitle="Activates/renews the plan and logs a manual payment">
        <form onSubmit={submit}>
          <Field label="Customer">
            <select className="select" style={{ width: '100%' }} value={userId} onChange={(e) => setUserId(e.target.value)} required>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
            </select>
          </Field>
          <Field label="Plan">
            <select className="select" style={{ width: '100%' }} value={planSlug} onChange={(e) => { setPlanSlug(e.target.value); syncAmount(e.target.value, cycle); }} required>
              {plans.map((p) => <option key={p.slug} value={p.slug}>{p.name} — ${p.priceMonthlyUsd}/mo · ${p.priceYearlyUsd}/yr</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Billing cycle">
              <select className="select" style={{ width: '100%' }} value={cycle} onChange={(e) => { const c = e.target.value as 'monthly' | 'yearly'; setCycle(c); syncAmount(planSlug, c); }}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </Field>
            <Field label="Amount paid (USD)">
              <input type="number" min="0.01" step="0.01" className="input" style={{ width: '100%' }} value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} required />
            </Field>
          </div>
          <Field label="Notes (invoice #, method, reference)">
            <input type="text" className="input" style={{ width: '100%' }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Invoice INV-1042, cash" />
          </Field>
          {formError && <div style={{ color: 'var(--danger-text)', fontSize: 12, marginBottom: 10 }}>{formError}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : <DollarSign size={14} />} <span>Record & activate</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);
